import { ApiError, type View, type Transport } from "../transport";
import type { LaunchLocale } from "../../../locales/src/locales";
/** Transient read cache only. Never persist cards, private bindings, credentials
 * or drafts; only current server actions/material from the LATEST page survive.
 * It is not authority for commands or a claim that older rows remain eligible. */
export class OrderPages {
  private identity = "";
  private cards = new Map<string, View["items"][number]>();
  reset() {
    this.identity = "";
    this.cards.clear();
  }
  accept(epoch: number, path: string, page: View): View {
    const identity = JSON.stringify([epoch, path, page.id]);
    if (identity !== this.identity) {
      this.reset();
      this.identity = identity;
    }
    for (const card of page.items) this.cards.set(card.id, card);
    return { ...page, items: Array.from(this.cards.values()) };
  }
}
/** Follow only the actual source-projected B10 opaque cursor on the same app
 * route. API source/current session checks still own the private read. Caller
 * clears onSessionRevoked/route/locale change and never substitutes cached data
 * after an error, epoch change or retained-policy rejection. */
export async function loadOrderContinuation(
  api: Transport,
  pages: OrderPages,
  current: View,
  path: string,
  locale: LaunchLocale,
  signal?: AbortSignal,
): Promise<View> {
  const epoch = api.sessionEpoch;
  try {
    const next = current.next;
    if (
      typeof next !== "string" ||
      !next.startsWith("/") ||
      next.startsWith("//")
    )
      throw new ApiError("stale", "INVALID_ORDER_CONTINUATION");
    const url = new URL(next, api.origin),
      keys = [...url.searchParams.keys()];
    if (
      url.origin !== api.origin ||
      url.pathname !== path ||
      url.hash ||
      keys.length !== 1 ||
      keys[0] !== "cursor" ||
      !url.searchParams.get("cursor")
    )
      throw new ApiError("stale", "INVALID_ORDER_CONTINUATION");
    const page = await api.read("B10", path, locale, url.search, signal);
    if (epoch !== api.sessionEpoch || signal?.aborted)
      throw new ApiError("denied", "SESSION_CHANGED");
    return pages.accept(epoch, path, page);
  } catch (error) {
    pages.reset();
    throw error;
  }
}
